import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTenantContext } from '../../context/TenantContext';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useToast } from '../../hooks/useToast';
import { FooterSlot } from '../templates/FooterSlot';
import StudentSidebar from './dashboard/StudentSidebar';

interface StudentLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  showHeader?: boolean;
  headerContent?: React.ReactNode;
  className?: string;
  // Callback pour passer le profil étudiant aux enfants
  onStudentProfileLoaded?: (profile: any) => void;
}

const StudentLayout: React.FC<StudentLayoutProps> = ({
  children,
  title = 'E-Learning',
  description,
  showHeader = true,
  headerContent,
  className = '',
  onStudentProfileLoaded
}) => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('elearning_student_token');
      if (token && tenant?.id) {
        try {
          const profile = await getStudentProfileByToken(token);
          if (profile) {
            setStudentProfile(profile);
            // Appeler le callback pour passer le profil aux enfants
            if (onStudentProfileLoaded) {
              onStudentProfileLoaded(profile);
            }
          } else {
            // Token invalide ou expiré
            localStorage.removeItem('elearning_student_token');
            navigate('/elearning/auth', { state: { redirectTo: window.location.pathname } });
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('elearning_student_token');
          navigate('/elearning/auth', { state: { redirectTo: window.location.pathname } });
        }
      } else {
        navigate('/elearning/auth', { state: { redirectTo: window.location.pathname } });
      }
      setIsLoading(false);
    };
    
    checkAuthentication();
  }, [tenant?.id, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('elearning_student_token');
    navigate('/elearning/auth');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!studentProfile) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Fond d'écran et son overlay */}
      <div 
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: tenant ? `url(${tenant.config?.bg || '/default-bg.jpg'})` : 'url(/default-bg.jpg)',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-black/90 to-gray-900/95 backdrop-blur-[2px]"></div>
      </div>
      
      {/* Motif en superposition */}
      <div 
        className="fixed inset-0 z-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px'
        }}
      />
      
      {/* Sidebar - Affichage conditionnel pour desktop et mobile */}
      <StudentSidebar 
        studentProfile={studentProfile}
        onLogout={handleLogout}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Contenu principal avec marge pour la sidebar */}
      <div className={`relative z-10 ${sidebarCollapsed ? 'lg:pl-24' : 'lg:pl-72'}`}>
        <div className={`p-6 ${className}`}>
          {/* Header optionnel */}
          {showHeader && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {title}
                  </h1>
                  {description && (
                    <p className="text-gray-600 dark:text-gray-400">
                      {description}
                    </p>
                  )}
                </div>
                {headerContent}
              </div>
            </motion.div>
          )}

          {/* Contenu principal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {children}
          </motion.div>
        </div>
        
        {/* Footer unifié avec l'admin */}
        <FooterSlot />
      </div>
    </div>
  );
};

export default StudentLayout;
